import { type MeDto, type SystemModulesDto } from '@bqcrm/shared';
import { Badge, Group, Stack, Switch, Text } from '@mantine/core';
import { IconBuildingWarehouse, IconCar, IconLock } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api } from '../api';
import { can } from '../auth';
import { ask } from '../confirm';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState, SectionCard } from '../components/ui';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';

type Code = keyof SystemModulesDto;

/** Moduły opcjonalne — jeden przełącznik na cały system (obie firmy); dostęp osób — uprawnieniami modułu. */
const MODULES: { code: Code; title: string; icon: ReactNode; what: string; off: string; enabledNote?: string }[] = [
  {
    code: 'flota',
    title: 'Flota',
    icon: <IconCar size={16} />,
    what: 'Samochody firmowe: terminy, serwisy, zgłoszenia usterek, opony, przebieg, niedostępność i koszty.',
    off: 'Moduł zniknie z menu, kalendarza, planowania, faktur zakupu i aplikacji mobilnej. Dane floty zostają — po ponownym włączeniu wszystko wróci.',
  },
  {
    code: 'magazyn',
    title: 'Magazyn',
    icon: <IconBuildingWarehouse size={16} />,
    what: 'Stany, zapotrzebowania z umów i od ekip, pakiety, zamówienia do dostawców, przyjęcia z faktur KSeF, gotowość materiałowa w planowaniu.',
    off: 'Magazyn zniknie z menu, spraw, planowania, katalogu i aplikacji mobilnej; system będzie działał jak bez magazynu. Dane zostają — po ponownym włączeniu wszystko wróci.',
    enabledNote: 'Potrzeby z umów podpisanych przed włączeniem tworzy się w sprawie („Utwórz teraz”).',
  },
];

/** Ustawienia → Moduły dodatkowe (docs/14 §14, docs/19 §3): włączanie i wyłączanie modułów opcjonalnych. */
export function SystemModulesPage({ me }: { me: MeDto }) {
  const allowed = can(me, 'moduly.konfiguracja', 'W');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['system-modules'], queryFn: () => api<SystemModulesDto>('/system-modules') });
  const save = useMutation({
    mutationFn: (body: Partial<Record<Code, boolean>>) => api<SystemModulesDto>('/system-modules', { method: 'PUT', body }),
    onSuccess: (r, body) => {
      qc.setQueryData(['system-modules'], r);
      const [code, on] = Object.entries(body)[0] as [Code, boolean];
      notifyOk(`${on ? 'Włączono' : 'Wyłączono'} moduł ${MODULES.find((m) => m.code === code)?.title ?? code}`);
      // menu, uprawnienia, sekcje i procesy zależą od modułów — odświeżenie profilu i wszystkich widoków
      void qc.invalidateQueries();
    },
    onError: notifyError,
  });
  const toggle = async (m: (typeof MODULES)[number], on: boolean) => {
    if (!on && !(await ask(m.off, { title: `Wyłączyć moduł ${m.title}?`, confirm: 'Wyłącz', danger: true }))) return;
    save.mutate({ [m.code]: on });
  };
  return (
    <>
      <PageHeader title="Moduły dodatkowe" description="Moduły opcjonalne całego systemu (obu firm). Wyłączony moduł znika z menu, ekranów i procesów — system działa jak bez niego, a jego dane zostają w bazie." />
      {!allowed ? (
        <EmptyState icon={<IconLock size={24} />} title="Brak uprawnień" hint="Moduły włącza i wyłącza właściciel (uprawnienie „Moduły dodatkowe”)." />
      ) : (
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Stack maw={720}>
            {MODULES.map((m) => {
              const st = q.data?.[m.code];
              const on = !!st?.enabled;
              return (
                <SectionCard
                  key={m.code}
                  title={m.title}
                  icon={m.icon}
                  actions={
                    <Group gap="sm" wrap="nowrap">
                      <Badge variant="light" color={on ? 'teal' : 'gray'}>
                        {on ? 'włączony' : 'wyłączony'}
                      </Badge>
                      <Switch size="md" checked={on} disabled={!q.data || save.isPending} onChange={(e) => void toggle(m, e.currentTarget.checked)} aria-label={`Moduł ${m.title}`} />
                    </Group>
                  }
                >
                  <Text size="sm">{m.what}</Text>
                  {on && st?.enabledAt && (
                    <Text size="xs" c="dimmed" mt={6}>
                      Włączony {formatDateTime(st.enabledAt)}.{m.enabledNote ? ` ${m.enabledNote}` : ''}
                    </Text>
                  )}
                </SectionCard>
              );
            })}
          </Stack>
        </QueryState>
      )}
    </>
  );
}
